import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CreateButton, useCanAccess, useDataProvider, useGetList, useNotify, useStore } from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';
import { useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Button,
    Collapse,
    Grow,
    IconButton,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import type { DropResult } from '@hello-pangea/dnd';
import { Title } from 'react-admin';
import type { Card, CardStatus } from '@/types';
import { PENDING, WORKING, DONE, canTransition } from './cardStatus';
import { CardTile } from './CardTile';

type ScopeFilter = 'all' | 'assigned_to_me' | 'created_by_me';

interface StatusColumn {
    status: CardStatus;
    label: string;
    accent: string;
}

const STATUS_COLUMNS: StatusColumn[] = [
    { status: PENDING, label: 'Pending', accent: '#f59e0b' },
    { status: WORKING, label: 'Working', accent: '#3b82f6' },
    { status: DONE, label: 'Done', accent: '#10b981' },
];

interface CardBoardProps {
    /** Hide the board's own "New Job" button when the page already provides one. */
    hideCreate?: boolean;
}

export const CardBoard = ({ hideCreate = false }: CardBoardProps) => {
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // Persisted so the dialog routes (/cards/create, /cards/:id/show), which remount
    // this board behind the modal, don't reset the filter the user set.
    const [scope, setScope] = useStore<ScopeFilter>('cards.boardScope', 'all');
    const [search, setSearch] = useStore<string>('cards.boardSearch', '');
    const [searchOpen, setSearchOpen] = useState(() => search.length > 0);

    const { canAccess: canCreate } = useCanAccess({
        resource: 'cards',
        action: 'create',
    });

    const scopeFilter: Record<string, any> =
        scope === 'assigned_to_me'
            ? { assigned_to: 'me' }
            : scope === 'created_by_me'
              ? { created_by: 'me' }
              : {};

    const filter = {
        ...scopeFilter,
        ...(search ? { q: search } : {}),
    };

    // Disable background refetches so the three columns don't race against the optimistic
    // drag-and-drop state. The initial fetch still runs, and changing scope/search creates a
    // new query key which triggers a fresh fetch — those paths are untouched. What we avoid
    // here is React Query's default refetch-on-focus/mount/reconnect, which would otherwise
    // return a stale snapshot (status-change goes through a raw fetch, bypassing the cache)
    // and clobber the just-moved card back to its previous column.
    const listQueryOptions = {
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    };

    const { data: fetchedPendingData } = useGetList(
        'cards',
        {
            filter: { ...filter, status: PENDING },
            sort: { field: 'position', order: 'ASC' },
            pagination: { page: 1, perPage: 100 },
        },
        listQueryOptions
    );

    const { data: fetchedWorkingData } = useGetList(
        'cards',
        {
            filter: { ...filter, status: WORKING },
            sort: { field: 'position', order: 'ASC' },
            pagination: { page: 1, perPage: 100 },
        },
        listQueryOptions
    );

    const { data: doneData, total: doneTotal } = useGetList(
        'cards',
        {
            filter: { ...filter, status: DONE },
            sort: { field: 'position', order: 'ASC' },
            pagination: { page: 1, perPage: 30 },
        },
        listQueryOptions
    );

    const [pendingData, setPendingData] = useState<Card[]>(fetchedPendingData || []);
    const [workingData, setWorkingData] = useState<Card[]>(fetchedWorkingData || []);
    const [localDone, setLocalDone] = useState<Card[]>(doneData || []);
    const [doneShowCount, setDoneShowCount] = useState(30);

    // Reconcile local state only when the FETCHED reference changes (a real server fetch /
    // filter change). Using useEffect ensures optimistic local mutations don't trigger a
    // reconcile — otherwise a just-moved card would snap back to its previous column.
    useEffect(() => {
        if (fetchedPendingData) setPendingData(fetchedPendingData);
    }, [fetchedPendingData]);
    useEffect(() => {
        if (fetchedWorkingData) setWorkingData(fetchedWorkingData);
    }, [fetchedWorkingData]);
    useEffect(() => {
        if (doneData) setLocalDone(doneData);
    }, [doneData]);

    const visibleDone = useMemo(
        () => localDone.slice(0, doneShowCount),
        [localDone, doneShowCount]
    );

    const doneHasMore = (doneTotal || 0) > visibleDone.length;

    const countFor = (status: CardStatus): number => {
        if (status === PENDING) return pendingData?.length ?? 0;
        if (status === WORKING) return workingData?.length ?? 0;
        return visibleDone.length;
    };

    const cardsFor = (status: CardStatus): Card[] => {
        if (status === PENDING) return pendingData || [];
        if (status === WORKING) return workingData || [];
        return visibleDone;
    };

    const setterFor = (status: CardStatus) =>
        status === PENDING
            ? setPendingData
            : status === WORKING
              ? setWorkingData
              : setLocalDone;

    const handleDragEnd = (result: DropResult) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;
        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }

        const cardId = parseInt(draggableId, 10);
        const sourceStatus = parseInt(source.droppableId, 10) as CardStatus;
        const destStatus = parseInt(destination.droppableId, 10) as CardStatus;

        if (!canTransition(sourceStatus, destStatus)) {
            notify('Invalid status transition', { type: 'error' });
            return;
        }

        const allCards = [...(pendingData || []), ...(workingData || []), ...localDone];
        const card = allCards.find(c => c.id === cardId);
        if (!card) return;

        const originalCard = card;
        const sourceIndex = source.index;

        // Build the destination list *as it will look after the move* so we can compute the
        // new position as the midpoint between the two neighbors at the drop index.
        const destList = cardsFor(destStatus).filter(c => c.id !== cardId);
        const insertAt = Math.min(destination.index, destList.length);
        const prevCard = insertAt > 0 ? destList[insertAt - 1] : null;
        const nextCard = insertAt < destList.length ? destList[insertAt] : null;
        const prevPos = typeof prevCard?.position === 'number' ? prevCard.position : null;
        const nextPos = typeof nextCard?.position === 'number' ? nextCard.position : null;

        let newPosition: number;
        if (prevPos !== null && nextPos !== null) {
            newPosition = (prevPos + nextPos) / 2;
        } else if (prevPos !== null) {
            newPosition = prevPos + 1024;
        } else if (nextPos !== null) {
            newPosition = nextPos - 1024;
        } else {
            newPosition = 0;
        }

        // Optimistic move: remove from source, insert into destination at the drop index.
        const setSource = setterFor(sourceStatus);
        const setDest = setterFor(destStatus);
        const sameColumn = sourceStatus === destStatus;

        const updatedCard = { ...originalCard, status: destStatus, position: newPosition };

        if (sameColumn) {
            setDest(prev => {
                const next = prev.filter(c => c.id !== cardId);
                next.splice(insertAt, 0, updatedCard);
                return next;
            });
        } else {
            setSource(prev => prev.filter(c => c.id !== cardId));
            setDest(prev => {
                const next = prev.filter(c => c.id !== cardId);
                next.splice(insertAt, 0, updatedCard);
                return next;
            });
        }

        // Fire-and-forget. On success, trust the optimistic state (no refetch, no jump) and
        // patch the react-admin getOne cache so the detail modal reads the fresh status.
        // On failure, revert the card to its original column and index.
        dataProvider.updateCardStatus(cardId, destStatus, newPosition).then((serverCard: Card) => {
            queryClient.setQueryData<Card | undefined>(
                ['cards', 'getOne', { id: String(cardId), meta: undefined }],
                prev => (prev ? { ...prev, ...serverCard } : serverCard)
            );
        }).catch((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Failed to move card';
            notify(message, { type: 'error' });

            if (sameColumn) {
                setDest(prev => {
                    const next = prev.filter(c => c.id !== cardId);
                    const at = Math.min(sourceIndex, next.length);
                    next.splice(at, 0, originalCard);
                    return next;
                });
            } else {
                setDest(prev => prev.filter(c => c.id !== cardId));
                setSource(prev => {
                    const next = prev.filter(c => c.id !== cardId);
                    const at = Math.min(sourceIndex, next.length);
                    next.splice(at, 0, originalCard);
                    return next;
                });
            }
        });
    };

    const totalCards =
        (pendingData?.length ?? 0) + (workingData?.length ?? 0) + visibleDone.length;

    return (
        <Box
            sx={{
                py: 3,
                px: { xs: 2, sm: 3 },
                minHeight: '100%',
                background: theme =>
                    `radial-gradient(ellipse at top, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 60%)`,
            }}
        >
            <Title title="Job Board" />

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 3, gap: 2 }}
            >
                <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                        {totalCards} card{totalCards === 1 ? '' : 's'} visible
                    </Typography>
                </Box>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                    <ToggleButtonGroup
                        value={scope}
                        exclusive
                        onChange={(_, newScope) => {
                            if (newScope) setScope(newScope);
                        }}
                        size="small"
                        sx={{
                            borderRadius: 999,
                            '& .MuiToggleButton-root': {
                                border: 'none',
                                px: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '999px !important',
                                mx: 0.25,
                                transition: 'all 160ms ease',
                            },
                            '& .MuiToggleButton-root.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': {
                                    backgroundColor: 'primary.dark',
                                    color: 'primary.contrastText',
                                },
                            },
                        }}
                    >
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="assigned_to_me">Mine</ToggleButton>
                        <ToggleButton value="created_by_me">Created</ToggleButton>
                    </ToggleButtonGroup>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton
                            onClick={() => {
                                setSearchOpen(prev => {
                                    if (prev) setSearch('');
                                    return !prev;
                                });
                            }}
                            sx={{
                                transition: 'transform 160ms ease, background 160ms ease',
                                bgcolor: searchOpen ? 'action.selected' : 'transparent',
                                '&:hover': { transform: 'scale(1.08)' },
                            }}
                        >
                            {searchOpen ? <CloseIcon /> : <SearchIcon />}
                        </IconButton>
                        <Collapse in={searchOpen} orientation="horizontal" timeout={240}>
                            <TextField
                                placeholder="Search cards..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                variant="outlined"
                                sx={{
                                    width: { xs: 180, sm: 240 },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 999,
                                    },
                                }}
                            />
                        </Collapse>
                    </Stack>

                    {canCreate && !hideCreate && (
                        <CreateButton
                            resource="cards"
                            variant="contained"
                            label="New Job"
                            sx={{ marginLeft: 2 }}
                        />
                    )}
                </Stack>
            </Stack>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{ minHeight: '60vh' }}
                >
                    {STATUS_COLUMNS.map(col => (
                        <Droppable
                            key={col.status}
                            droppableId={String(col.status)}
                            renderClone={(dragProvided, dragSnapshot, rubric) => {
                                const sourceCards = cardsFor(col.status);
                                const card = sourceCards[rubric.source.index];
                                const node = (
                                    <div
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        {...dragProvided.dragHandleProps}
                                        style={{
                                            ...dragProvided.draggableProps.style,
                                        }}
                                    >
                                        {card && (
                                            <CardTile
                                                card={card}
                                                isDragging={dragSnapshot.isDragging}
                                            />
                                        )}
                                    </div>
                                );
                                return createPortal(node, document.body);
                            }}
                        >
                            {(provided, snapshot) => (
                                <Paper
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    elevation={0}
                                    sx={{
                                        flex: 1,
                                        minWidth: 300,
                                        p: 2,
                                        position: 'relative',
                                        borderRadius: 3,
                                        border: '1px solid',
                                        borderColor: snapshot.isDraggingOver
                                            ? alpha(col.accent, 0.6)
                                            : 'divider',
                                        bgcolor: theme =>
                                            snapshot.isDraggingOver
                                                ? alpha(col.accent, 0.06)
                                                : alpha(theme.palette.background.paper, 0.6),
                                        backdropFilter: 'blur(6px)',
                                        minHeight: 400,
                                        overflowY: 'auto',
                                        maxHeight: 'calc(100vh - 300px)',
                                        transition:
                                            'background-color 220ms ease, border-color 220ms ease, box-shadow 220ms ease',
                                        boxShadow: snapshot.isDraggingOver
                                            ? `0 0 0 4px ${alpha(col.accent, 0.12)}, 0 10px 24px ${alpha(col.accent, 0.18)}`
                                            : 'none',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: 3,
                                            borderTopLeftRadius: 12,
                                            borderTopRightRadius: 12,
                                            background: `linear-gradient(90deg, ${col.accent}, ${alpha(col.accent, 0.5)})`,
                                        },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ mb: 2, mt: 0.5 }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: col.accent,
                                                    boxShadow: `0 0 0 3px ${alpha(col.accent, 0.2)}`,
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 700,
                                                    letterSpacing: 1,
                                                    color: 'text.secondary',
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                {col.label}
                                            </Typography>
                                        </Stack>
                                        <Box
                                            sx={{
                                                px: 1,
                                                minWidth: 24,
                                                height: 22,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 999,
                                                fontSize: '0.72rem',
                                                fontWeight: 700,
                                                color: col.accent,
                                                bgcolor: alpha(col.accent, 0.12),
                                            }}
                                        >
                                            {countFor(col.status)}
                                        </Box>
                                    </Stack>

                                    <Stack spacing={1.25}>
                                        {cardsFor(col.status).map((card, index) => (
                                            <Draggable
                                                key={card.id}
                                                draggableId={String(card.id)}
                                                index={index}
                                            >
                                                {(dragProvided, dragSnapshot) => (
                                                    <Grow
                                                        in
                                                        timeout={240 + Math.min(index, 10) * 30}
                                                    >
                                                        <div
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            {...dragProvided.dragHandleProps}
                                                            style={{
                                                                ...dragProvided.draggableProps.style,
                                                            }}
                                                        >
                                                            <CardTile
                                                                card={card}
                                                                isDragging={dragSnapshot.isDragging}
                                                            />
                                                        </div>
                                                    </Grow>
                                                )}
                                            </Draggable>
                                        ))}
                                    </Stack>

                                    {cardsFor(col.status).length === 0 && (
                                        <Box
                                            sx={{
                                                mt: 2,
                                                py: 4,
                                                textAlign: 'center',
                                                color: 'text.disabled',
                                                fontSize: '0.8rem',
                                                border: '1px dashed',
                                                borderColor: alpha(col.accent, 0.3),
                                                borderRadius: 2,
                                            }}
                                        >
                                            No cards
                                        </Box>
                                    )}

                                    {col.status === DONE && doneHasMore && (
                                        <Button
                                            variant="text"
                                            fullWidth
                                            sx={{
                                                mt: 2,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                color: col.accent,
                                            }}
                                            onClick={() =>
                                                setDoneShowCount(prev => prev + 30)
                                            }
                                        >
                                            Show more
                                        </Button>
                                    )}
                                </Paper>
                            )}
                        </Droppable>
                    ))}
                </Stack>
            </DragDropContext>
        </Box>
    );
};

CardBoard.displayName = 'CardBoard';
